import React from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import AllHealthSm from '../svgs/all-health-logo-sm.svg';
import AllHealth from '../svgs/all-health-logo.svg';
import { styles, colors, device } from '../styles/globals';
import { clearSession } from '../services/msal';

const AllHealthLogo = styled(AllHealth)`
  @media ${device.mobileL} {
    display: none;
  }
`;

const AllHealthLogoSm = styled(AllHealthSm)`
  display: none;
  @media ${device.mobileL} {
    display: block;
  }
`;

const FooterEl = styled.footer`
  background-color: ${styles.footerBackground};
  color: ${colors.white};
  min-height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media ${device.tabletS} {
    min-height: 372px;
  }

  @media ${device.mobileL} {
    min-height: 288px;
  }
`;

const FooterWrap = styled.div`
  width: 100%;
  padding: 96px 15%;
  display: flex;
  align-items: center;

  @media ${device.desktopS} {
    padding: 96px 10%;
  }

  @media ${device.laptopL} {
    padding: 96px 6%;
  }

  /* Stack the whole footer before the links get tight beside the logo: the
     single row stops fitting just under 1200px, and squeezing it there breaks
     the copyright mid-phrase and orphans the last links on their own line. */
  @media ${device.laptopM} {
    flex-direction: column;
  }

  @media ${device.tablet} {
    padding: 76px 6%;
  }

  @media ${device.tabletS} {
    padding: 60px 55px;
    align-items: flex-start;
  }

  @media ${device.mobileL} {
    padding: 72px 35px;
  }
`;

const FooterRightsWrap = styled.div`
  display: flex;
  align-items: center;

  /* Matches the width at which FooterWrap stacks. */
  @media ${device.laptopM} {
    margin-bottom: 18px;
  }

  @media ${device.mobileL} {
    align-items: flex-start;
  }
`;

const FooterRights = styled.p`
  font-size: 14px;
  margin-left: 27px;
  margin-right: 41px;
  opacity: 0.5;
  white-space: nowrap;

  @media ${device.tabletS} {
    margin-right: 0;
    margin-left: 18px;
  }

  @media ${device.mobileL} {
    font-size: 12px;
  }
`;

const FooterNav = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  flex-grow: 1;
  gap: 12px 18px;

  a {
    opacity: 0.5;
    font-size: 14px;
    &:hover {
      opacity: 1;
    }

    @media ${device.mobileL} {
      font-size: 12px;
    }
  }

  /* Once stacked the links are centred under the logo rather than pushed to
     the right edge. */
  @media ${device.laptopM} {
    justify-content: center;
  }

  @media ${device.tabletS} {
    justify-content: flex-start;

    a {
      opacity: 1;
    }
  }
`;

const Footer = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const currentYear = new Date().getFullYear();

  const handleSignOut = (event) => {
    event.preventDefault();
    clearSession();
    instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/`,
    });
  };

  return (
    <FooterEl>
      <FooterWrap>
        <FooterRightsWrap>
          <Link to="/">
            <AllHealthLogo />
            <AllHealthLogoSm />
          </Link>
          <FooterRights>
            &copy;
            {' '}
            {currentYear}
            {' '}
            all.health
          </FooterRights>
        </FooterRightsWrap>
        <FooterNav>
          <Link to="/">
            home
          </Link>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://jobs.lever.co/all.health"
          >
            careers
          </a>
          {isAuthenticated && (
            <Link to="/faq">
              FAQ
            </Link>
          )}
          <Link to="/terms">
            terms of service
          </Link>
          <Link to="/privacy">
            privacy policy
          </Link>
          <Link to="/privacy#ca-notice-at-collection">
            CA notice at collection
          </Link>
          <Link to="/contact-us">
            contact us
          </Link>
          {isAuthenticated ? (
            <Link to="/" onClick={handleSignOut}>
              sign out
            </Link>
          ) : (
            <Link to="/sign">
              sign in
            </Link>
          )}
        </FooterNav>
      </FooterWrap>
    </FooterEl>
  );
};

export default Footer;
